import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, StyleSheet, Text, Pressable, ActivityIndicator, TextInput } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import PlacesAutocomplete, { Prediction } from '../components/google-places-autocomplete';
import { GetGoogleGeocode, getAddressComponentsFromGeodata } from '../components/common/utils/google-api-utils';
import { getUserInfo, updateBillingAddressToServer } from '../services/user-service';
import { isSuccessful } from '../services/api-requests';
import { notificationService } from '../services/notification-service';
import { useDispatch } from 'react-redux';
import { userActions } from '../store/slices/user-slice';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import type { RootStackParamList } from '../navigation/stackTypes';
import { MaterialCommunityIcons } from '@expo/vector-icons';

type Props = NativeStackScreenProps<RootStackParamList, 'SetupBillingAddress'>;

type AddressState = {
  full_address: string;
  address: string; // street_number + street_name
  address_line_2: string;
  street_name: string;
  street_number: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
};

const initialAddress: AddressState = {
  full_address: '',
  address: '',
  address_line_2: '',
  street_name: '',
  street_number: '',
  city: '',
  state: '',
  zip_code: '',
  country: '',
};

const SetupBillingAddress: React.FC<Props> = ({ navigation }) => {
  const dispatch = useDispatch();
  const [addr, setAddr] = useState<AddressState>(initialAddress);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await getUserInfo();
        if (isSuccessful(res.status) && res.payload?.user) {
          const u: any = res.payload.user as any;
          const prefill: AddressState = {
            full_address: u.full_address || u.address || '',
            address: u.full_address ? u.full_address.split(',')[0] : (u.address || ''),
            address_line_2: u.address_line_2 || '',
            street_name: u.street_name || '',
            street_number: u.street_number || '',
            city: u.city || '',
            state: u.state || '',
            zip_code: u.zip_code || '',
            country: u.country || '',
          };
          setAddr(prefill);
        }
      } catch (e) {
        // noop
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const onPlaceSelected = useCallback(async (place: Prediction) => {
    try {
      setError(null);
      const geo = await GetGoogleGeocode(place.description);
      if (!geo) return;
      const comps = getAddressComponentsFromGeodata(geo);
      const addressLine = `${comps.street_number ?? ''}${comps.street_number ? ' ' : ''}${comps.street_name ?? ''}`.trim();

      setAddr(prev => ({
        ...prev,
        full_address: place.description,
        address: addressLine,
        // Keep existing address_line_2 when selecting new address
        street_name: comps.street_name ?? '',
        street_number: comps.street_number ?? '',
        city: comps.city ?? '',
        state: comps.state ?? '',
        zip_code: comps.zip_code ?? '',
        country: comps.country ?? '',
      }));
      
      // Warn if street number is missing
      if (!comps.street_number) {
        setError('Street number is missing. Please click Edit to add it manually.');
        setIsEditing(true);
      }
    } catch (e) {
      setError('Unable to resolve selected address.');
    }
  }, []);

  const canSave = useMemo(() => {
    return Boolean(
      addr.full_address && 
      addr.street_number && 
      addr.street_name && 
      addr.city && 
      addr.state && 
      addr.zip_code && 
      addr.country
    );
  }, [addr]);

  // Compare by value with initialAddress
  const isAddrInitial = (addrToCheck: typeof addr) =>
    Object.keys(initialAddress).every(
      key => addrToCheck[key as keyof typeof addrToCheck] === initialAddress[key as keyof typeof initialAddress]
    );

  const onSave = useCallback(async () => {
    if (!canSave) {
      setError('Please fill in all required address fields.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        full_address: addr.full_address,
        address_line_2: addr.address_line_2,
        street_name: addr.street_name,
        street_number: addr.street_number,
        city: addr.city,
        state: addr.state,
        zip_code: addr.zip_code,
        country: addr.country,
      } as Record<string, any>;
      const resp = await updateBillingAddressToServer(payload);
      if (isSuccessful(resp.status)) {
        // Refresh user in store
        const refreshed = await getUserInfo();
        if (isSuccessful(refreshed.status)) {
          dispatch(userActions.setUserObj({ userObj: refreshed.payload?.user }));
        }
        notificationService.sendNotification('success', 'Billing address saved.');
        navigation.navigate('OnboardingStatus');
      } else {
        notificationService.sendNotification('error', resp.payload?.message || 'Failed to save address');
      }
    } catch (e) {
      notificationService.sendNotification('error', 'Failed to save address');
    } finally {
      setSaving(false);
    }
  }, [addr, canSave, dispatch, navigation]);

  if (loading) {
    return (
      <View style={styles.container}>
        <SafeAreaView style={styles.safeArea}>
          <View style={styles.centerWrap}>
            <ActivityIndicator size="large" color="#01556F" />
            <Text style={styles.loadingText}>Loading...</Text>
          </View>
        </SafeAreaView>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.header}>
          <Pressable
            onPress={() => navigation.navigate('OnboardingStatus')}
            style={({ pressed }) => [
              {
                marginRight: 12,
                opacity: pressed ? 0.5 : 1,
                padding: 4,
                zIndex: 2,
              },
            ]}
            accessibilityLabel="Back to Onboarding"
          >
            <MaterialCommunityIcons
              name="chevron-left"
              size={32}
              color="white"
            />
          </Pressable>
          <Text style={styles.titleAbsolute}>Setup Billing Address</Text>
        </View>
        <View style={styles.content}>
          <PlacesAutocomplete
            headerText="Billing Address"
            headerStyle={styles.title}
            value={addr.full_address}
            onPlaceSelected={onPlaceSelected}
            error={error || undefined}
          />

          {isAddrInitial(addr) ? (
            <Text style={styles.title}>Please input a billing address</Text>
          ) : (
            <>
            <View style={styles.card}>
              <View style={styles.editToggleRow}>
                <Pressable
                  onPress={() => setIsEditing((prev) => !prev)}
                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                  style={({ pressed }) => [{ opacity: pressed ? 0.7 : 1, flexDirection: 'row', alignItems: 'center' }]}
                >
                  <MaterialCommunityIcons name={isEditing ? 'pencil-off' : 'pencil'} size={18} color="#FFFFFF" />
                  <Text style={styles.editText}>{isEditing ? 'Done' : 'Edit'}</Text>
                </Pressable>
              </View>
              <Row label="Street Number">
                {isEditing ? (
                  <TextInput
                    style={styles.input}
                    placeholder="123"
                    placeholderTextColor="#8D8E90"
                    value={addr.street_number}
                    onChangeText={(text) => setAddr(prev => ({ ...prev, street_number: text }))}
                    maxLength={20}
                  />
                ) : (
                  <Text style={styles.rowValue}>{addr.street_number || '-'}</Text>
                )}
              </Row>
              <Row label="Street Name">
                {isEditing ? (
                  <TextInput
                    style={styles.input}
                    placeholder="Main Street"
                    placeholderTextColor="#8D8E90"
                    value={addr.street_name}
                    onChangeText={(text) => setAddr(prev => ({ ...prev, street_name: text }))}
                    maxLength={100}
                  />
                ) : (
                  <Text style={styles.rowValue}>{addr.street_name || '-'}</Text>
                )}
              </Row>
              <Row label="Apt/Suite/Unit">
                <TextInput
                  style={styles.input}
                  placeholder="Apartment, Suite, Unit, etc."
                  placeholderTextColor="#8D8E90"
                  value={addr.address_line_2}
                  onChangeText={(text) => setAddr(prev => ({ ...prev, address_line_2: text }))}
                  maxLength={100}
                />
              </Row>
              <Row label="City">
                {isEditing ? (
                  <TextInput
                    style={styles.input}
                    placeholder="City"
                    placeholderTextColor="#8D8E90"
                    value={addr.city}
                    onChangeText={(text) => setAddr(prev => ({ ...prev, city: text }))}
                  />
                ) : (
                  <Text style={styles.rowValue}>{addr.city || '-'}</Text>
                )}
              </Row>
              <Row label="State">
                {isEditing ? (
                  <TextInput
                    style={styles.input}
                    placeholder="State"
                    placeholderTextColor="#8D8E90"
                    value={addr.state}
                    onChangeText={(text) => setAddr(prev => ({ ...prev, state: text }))}
                    autoCapitalize="characters"
                    maxLength={30}
                  />
                ) : (
                  <Text style={styles.rowValue}>{addr.state || '-'}</Text>
                )}
              </Row>
              <Row label="ZIP">
                {isEditing ? (
                  <TextInput
                    style={styles.input}
                    placeholder="ZIP Code"
                    placeholderTextColor="#8D8E90"
                    value={addr.zip_code}
                    onChangeText={(text) => setAddr(prev => ({ ...prev, zip_code: text }))}
                    keyboardType="numeric"
                    maxLength={10}
                  />
                ) : (
                  <Text style={styles.rowValue}>{addr.zip_code || '-'}</Text>
                )}
              </Row>
              <Row label="Country">
                <Text style={styles.rowValue}>{addr.country || '-'}</Text>
              </Row>
            </View>

            <Pressable
              onPress={onSave}
              disabled={!canSave || saving}
              style={({ pressed }) => [
                styles.saveButton,
                { opacity: (!canSave || saving || pressed) ? 0.7 : 1 },
              ]}
            >
              {saving ? (
                <ActivityIndicator color="#fff" />
              ) : (
                <Text style={styles.saveText}>Save</Text>
              )}
            </Pressable>
            </>
          )}
        </View>
            
      </SafeAreaView>
    </View>
  );
};

const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <View style={styles.row}>
    <Text style={styles.rowLabel}>{label}</Text>
    <View style={{ flexShrink: 1, marginLeft: 12 }}>
      {children}
    </View>
  </View>
);

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0B1E1D',
  },
  safeArea: {
    flex: 1,
  },
  content: {
    padding: 16,
  },
  title: {
    color: '#fff',
    fontSize: 22,
    fontWeight: '700',
  },
  header: {
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    position: 'relative',
    minHeight: 48,
  },
  titleAbsolute: {
    position: 'absolute',
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: 24,
    fontWeight: 'bold',
    color: 'white',
    zIndex: 1,
  },
  card: {
    backgroundColor: '#203D3C',
    borderRadius: 8,
    padding: 16,
    marginHorizontal: 16,
    marginVertical: 8,
  },
  editToggleRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginBottom: 8,
  },
  editText: {
    color: '#FFFFFF',
    marginLeft: 6,
    fontSize: 12,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  rowLabel: {
    color: '#FFFFFF',
    fontWeight: '600',
  },
  rowValue: {
    color: '#FFFFFF',
    marginLeft: 12,
    flexShrink: 1,
    textAlign: 'right',
  },
  input: {
    color: '#FFFFFF',
    backgroundColor: '#0B1E1D',
    borderWidth: 1,
    borderColor: '#438b88',
    borderRadius: 6,
    paddingVertical: 8,
    paddingHorizontal: 10,
    minWidth: 160,
    textAlign: 'right',
  },
  saveButton: {
    backgroundColor: '#39BEB7',
    marginTop: 16,
    paddingVertical: 12,
    borderRadius: 45,
    alignItems: 'center',
  },
  saveText: {
    color: '#fff',
    fontWeight: '600',
  },
  centerWrap: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    color: '#fff',
    marginTop: 12,
  },
});

export default SetupBillingAddress;


